import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SidebarLayout from "../shops/SidebarLayout";
import "../shops/shopStyles.css";
import EmailChangeModal from "../../components/EmailChangeModal";

const AdminProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch user profile on mount
  useEffect(() => {
    fetchUserProfile();
  }, []);

  const getApiBaseUrl = () => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setAlert({
        show: true,
        message: "Configuration error. Please contact support.",
        type: "error",
      });
      return null;
    }
    return baseURL;
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const response = await axios.get(`${baseURL}/api/users/profile`, {
        headers,
      });

      const user = response.data.user || response.data;
      setProfileData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || "",
      });

      setAlert({ show: false, message: "", type: "" });
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      setAlert({
        show: true,
        message:
          error.response?.data?.message || "Failed to load your profile",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.patch(`${baseURL}/api/users/profile`, profileData, {
        headers,
      });

      setAlert({
        show: true,
        message: "Profile updated successfully",
        type: "success",
      });

      // Refresh profile data
      setTimeout(() => {
        fetchUserProfile();
      }, 1500);
    } catch (error) {
      console.error("Failed to save profile:", error);
      setAlert({
        show: true,
        message:
          error.response?.data?.message || "Failed to update profile",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    // Validation
    if (!passwordData.currentPassword) {
      setAlert({
        show: true,
        message: "Please enter your current password",
        type: "error",
      });
      return;
    }

    if (!passwordData.newPassword) {
      setAlert({
        show: true,
        message: "Please enter a new password",
        type: "error",
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setAlert({
        show: true,
        message: "Passwords do not match",
        type: "error",
      });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setAlert({
        show: true,
        message: "Password must be at least 8 characters long",
        type: "error",
      });
      return;
    }

    try {
      setSaving(true);
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.post(
        `${baseURL}/api/users/change-password`,
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        },
        { headers }
      );

      setAlert({
        show: true,
        message: "Password changed successfully",
        type: "success",
      });

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setShowPasswordChangeModal(false);
    } catch (error) {
      console.error("Failed to change password:", error);
      setAlert({
        show: true,
        message:
          error.response?.data?.message || "Failed to change password",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="settings-container">
          <p>Loading your profile...</p>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="settings-container">
        <h1 className="settings-title">My Profile</h1>
        <p className="settings-subtitle">
          Manage your personal information and account settings
        </p>

        {/* Alert */}
        {alert.show && (
          <div
            style={{
              padding: "12px 16px",
              marginBottom: "20px",
              borderRadius: "8px",
              backgroundColor: alert.type === "success" ? "#d1fae5" : "#fee2e2",
              color: alert.type === "success" ? "#065f46" : "#991b1b",
              border: `1px solid ${
                alert.type === "success" ? "#a7f3d0" : "#fecaca"
              }`,
            }}
          >
            {alert.message}
          </div>
        )}

        {/* Personal Information Card */}
        <div className="settings-card">
          <div className="card-header">
            <h2>Personal Information</h2>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                name="firstName"
                value={profileData.firstName}
                onChange={handleProfileChange}
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={profileData.lastName}
                onChange={handleProfileChange}
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={profileData.email}
                disabled
                style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
              />
              <small style={{ color: "#6b7280", marginTop: "4px" }}>
                Email changes require verification. Contact support if needed.
              </small>
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={profileData.phone}
                onChange={handleProfileChange}
                placeholder="(000) 000-0000"
                disabled={saving}
              />
            </div>

            <button
              className="btn-primary"
              onClick={handleSaveProfile}
              disabled={saving}
              style={{ marginTop: "20px" }}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* Security Card */}
        <div className="settings-card">
          <div className="card-header">
            <h2>Security</h2>
          </div>
          <div className="card-body">
            <div style={{ marginBottom: "16px" }}>
              <h3 style={{ fontSize: "14px", fontWeight: "600" }}>Password</h3>
              <p style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px" }}>
                Change your password to secure your account
              </p>
            </div>
            <button
              className="btn-secondary"
              onClick={() => setShowPasswordChangeModal(true)}
              style={{ marginBottom: "20px" }}
            >
              Change Password
            </button>
          </div>
        </div>

        {/* Account Information */}
        <div className="settings-card">
          <div className="card-header">
            <h2>Account Information</h2>
          </div>
          <div className="card-body">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
              <div>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>Role</div>
                <div style={{ fontSize: "16px", fontWeight: "600", marginTop: "4px" }}>
                  Business Administrator
                </div>
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>
                  Account Status
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    marginTop: "4px",
                    color: "#059669",
                  }}
                >
                  Active
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordChangeModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000 }}>
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: -1,
            }}
            onClick={() => setShowPasswordChangeModal(false)}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "white",
              borderRadius: "12px",
              padding: isMobile ? "24px 16px" : "32px",
              maxWidth: isMobile ? "calc(100% - 32px)" : "400px",
              width: "100%",
              boxShadow: "0 20px 25px rgba(0, 0, 0, 0.1)",
              zIndex: 1001,
            }}
          >
            <h2 style={{ marginBottom: "20px", fontSize: "18px", fontWeight: "600" }}>
              Change Password
            </h2>

            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) =>
                  setPasswordData((prev) => ({
                    ...prev,
                    currentPassword: e.target.value,
                  }))
                }
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) =>
                  setPasswordData((prev) => ({
                    ...prev,
                    newPassword: e.target.value,
                  }))
                }
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) =>
                  setPasswordData((prev) => ({
                    ...prev,
                    confirmPassword: e.target.value,
                  }))
                }
                disabled={saving}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                marginTop: "24px",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="btn-secondary"
                onClick={() => setShowPasswordChangeModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handlePasswordChange}
                disabled={saving}
              >
                {saving ? "Updating..." : "Update Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </SidebarLayout>
  );
};

export default AdminProfile;
